/**
 * SaveLoadManager
 * 
 * Handles saving and loading game state to/from LocalStorage.
 * Features:
 * - Save to LocalStorage with key format: vn_save_${scenarioId}
 * - Load from LocalStorage
 * - Auto-cleanup of oldest saves when quota is exceeded
 * - Multiple save slots per scenario (future enhancement ready)
 */

import { GameState, SaveData } from '../types';

const SAVE_KEY_PREFIX = 'vn_save_';
const SAVE_LIST_KEY = 'vn_save_list';

export interface SaveMetadata {
  scenarioId: string;
  timestamp: number;
  key: string;
}

export interface SaveResult {
  success: boolean;
  error?: string;
  cleanedUpSaves?: string[];
}

export interface LoadResult {
  success: boolean;
  data?: SaveData;
  error?: string;
}

/**
 * Converts GameState to SaveData (serializable format)
 */
function gameStateToSaveData(scenarioId: string, gameState: GameState): SaveData {
  const { isAchievementAnimating: _, lastUnlockedAchievementId: __, ...persistedState } = gameState;
  return {
    scenarioId,
    timestamp: Date.now(),
    gameState: {
      ...persistedState,
      visitedScenes: Array.from(gameState.visitedScenes),
      hiddenElements: Array.from(gameState.hiddenElements || new Set())
    }
  };
}

/**
 * Converts SaveData back to GameState
 */
function saveDataToGameState(saveData: SaveData): GameState {
  const defaults: Partial<GameState> = {
    inventory: [],
    unlockedAchievements: [],
    unlockedSkills: [],
    flags: {},
    variables: {},
  };
  return {
    ...defaults,
    ...saveData.gameState,
    visitedScenes: new Set(saveData.gameState.visitedScenes || []),
    hiddenElements: new Set(saveData.gameState.hiddenElements || [])
  };
}

/**
 * Gets the save key for a scenario (and optional mission)
 */
function getSaveKey(scenarioId: string, missionId?: string): string {
  if (missionId) {
    return `${SAVE_KEY_PREFIX}${scenarioId}__${missionId}`;
  }
  return `${SAVE_KEY_PREFIX}${scenarioId}`;
}

/**
 * Gets list of all save metadata
 */
function getSaveList(): SaveMetadata[] {
  try {
    const listJson = localStorage.getItem(SAVE_LIST_KEY);
    if (!listJson) {
      return [];
    }
    return JSON.parse(listJson);
  } catch (error) {
    console.error('Error reading save list:', error);
    return [];
  }
}

/**
 * Updates the save list with new metadata
 */
function updateSaveList(metadata: SaveMetadata): void {
  const saveList = getSaveList();
  
  // Remove existing entry for this key if it exists
  const filteredList = saveList.filter(item => item.key !== metadata.key);
  
  // Add new entry
  filteredList.push(metadata);
  
  // Save updated list
  localStorage.setItem(SAVE_LIST_KEY, JSON.stringify(filteredList));
}

/**
 * Removes a save from the list
 */
function removeSaveFromList(key: string): void {
  const saveList = getSaveList();
  const filteredList = saveList.filter(item => item.key !== key);
  localStorage.setItem(SAVE_LIST_KEY, JSON.stringify(filteredList));
}

/**
 * Deletes the oldest save file
 */
function deleteOldestSave(): string | null {
  const saveList = getSaveList();
  
  if (saveList.length === 0) {
    return null;
  }
  
  // Sort by timestamp (oldest first)
  saveList.sort((a, b) => a.timestamp - b.timestamp);
  
  const oldestSave = saveList[0];
  
  if (!oldestSave) {
    return null;
  }
  
  // Delete from localStorage
  try {
    localStorage.removeItem(oldestSave.key);
    removeSaveFromList(oldestSave.key);
    console.log(`Deleted oldest save: ${oldestSave.key} (${new Date(oldestSave.timestamp).toLocaleString()})`);
    return oldestSave.scenarioId;
  } catch (error) {
    console.error('Error deleting oldest save:', error);
    return null;
  }
}

/**
 * Saves game state to LocalStorage
 */
export function saveGame(scenarioId: string, gameState: GameState): SaveResult {
  const saveData = gameStateToSaveData(scenarioId, gameState);
  const key = getSaveKey(scenarioId, gameState.missionId);
  const cleanedUpSaves: string[] = [];
  
  try {
    const serialized = JSON.stringify(saveData);
    
    // Try to save
    try {
      localStorage.setItem(key, serialized);
      
      // Update save list
      updateSaveList({
        scenarioId,
        timestamp: saveData.timestamp,
        key
      });
      
      return { success: true };
    } catch (error: any) {
      // Check if it's a quota exceeded error
      if (
        error.name === 'QuotaExceededError' ||
        error.code === 22 ||
        error.code === 1014 ||
        error.message?.includes('quota')
      ) {
        console.warn('LocalStorage quota exceeded, attempting cleanup...');
        
        // Try to free up space by deleting oldest saves
        let attempts = 0;
        const maxAttempts = 5;
        
        while (attempts < maxAttempts) {
          const deletedScenarioId = deleteOldestSave();
          
          if (deletedScenarioId) {
            cleanedUpSaves.push(deletedScenarioId);
          } else {
            // No more saves to delete
            break;
          }
          
          // Try saving again
          try {
            localStorage.setItem(key, serialized);
            
            // Update save list
            updateSaveList({
              scenarioId,
              timestamp: saveData.timestamp,
              key
            });
            
            return { 
              success: true, 
              cleanedUpSaves 
            };
          } catch (retryError) {
            // Still not enough space, continue loop
            attempts++;
          }
        }
        
        return {
          success: false,
          error: 'Unable to save: storage quota exceeded even after cleanup',
          cleanedUpSaves
        };
      }
      
      // Other error
      throw error;
    }
  } catch (error: any) {
    console.error('Error saving game:', error);
    return {
      success: false,
      error: error.message || 'Unknown error while saving'
    };
  }
}

/**
 * Loads game state from LocalStorage
 */
export function loadGame(scenarioId: string, missionId?: string): LoadResult {
  const key = getSaveKey(scenarioId, missionId);
  
  try {
    const serialized = localStorage.getItem(key);
    
    if (!serialized) {
      return {
        success: false,
        error: 'No save data found for this scenario'
      };
    }
    
    const saveData: SaveData = JSON.parse(serialized);
    
    // Validate save data
    if (saveData.scenarioId !== scenarioId) {
      return {
        success: false,
        error: 'Save data scenario ID mismatch'
      };
    }
    
    return {
      success: true,
      data: saveData
    };
  } catch (error: any) {
    console.error('Error loading game:', error);
    return {
      success: false,
      error: error.message || 'Unknown error while loading'
    };
  }
}

/**
 * Deletes a save file
 */
export function deleteSave(scenarioId: string): boolean {
  const key = getSaveKey(scenarioId);
  
  try {
    localStorage.removeItem(key);
    removeSaveFromList(key);
    return true;
  } catch (error) {
    console.error('Error deleting save:', error);
    return false;
  }
}

/**
 * Checks if a save exists for a scenario
 */
export function hasSave(scenarioId: string): boolean {
  const key = getSaveKey(scenarioId);
  return localStorage.getItem(key) !== null;
}

/**
 * Gets all save metadata
 */
export function listSaves(): SaveMetadata[] {
  return getSaveList();
}

/**
 * Clears all saves (use with caution)
 */
export function clearAllSaves(): void {
  const saveList = getSaveList();
  
  for (const save of saveList) {
    try {
      localStorage.removeItem(save.key);
    } catch (error) {
      console.error(`Error removing save ${save.key}:`, error);
    }
  }
  
  localStorage.removeItem(SAVE_LIST_KEY);
}

/**
 * SaveLoadManager class for convenience
 */
export class SaveLoadManager {
  /**
   * Saves game state to LocalStorage
   */
  static save(scenarioId: string, gameState: GameState): SaveResult {
    return saveGame(scenarioId, gameState);
  }

  /**
   * Loads game state from LocalStorage
   */
  static load(scenarioId: string, missionId?: string): LoadResult {
    return loadGame(scenarioId, missionId);
  }

  /**
   * Deletes a save file
   */
  static delete(scenarioId: string): boolean {
    return deleteSave(scenarioId);
  }

  /**
   * Checks if a save exists
   */
  static hasSave(scenarioId: string): boolean {
    return hasSave(scenarioId);
  }

  /**
   * Lists all saves
   */
  static listAll(): SaveMetadata[] {
    return listSaves();
  }

  /**
   * Clears all saves
   */
  static clearAll(): void {
    clearAllSaves();
  }

  /**
   * Converts SaveData to GameState
   */
  static saveDataToGameState(saveData: SaveData): GameState {
    return saveDataToGameState(saveData);
  }
}

